import React, { useEffect, useState } from 'react';
import { aiSummaryConfigService, aiProvidersService } from '../services/apiService';
import { AISummaryConfig, AIProvider } from '../types';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FormField } from '../components/ui/FormField';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/textarea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/apiService';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';

interface AISummaryConfigFormData {
  parameter_type_id: number | null;
  enabled: boolean;
  default_provider: number;
  prompt_template: string;
  model_name: string;
  temperature: number;
  max_tokens: number;
}

interface EditModalProps {
  config: AISummaryConfig | null;
  onSave: (data: Partial<AISummaryConfigFormData>) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  providers: AIProvider[];
  parameterTypes: any[];
}

const EditModal: React.FC<EditModalProps> = ({ config, onSave, onClose, isOpen, providers, parameterTypes }) => {
  const [formData, setFormData] = useState<Partial<AISummaryConfigFormData>>({
    parameter_type_id: null,
    enabled: false,
    default_provider: 0,
    prompt_template: '',
    model_name: 'gpt-4',
    temperature: 0.7,
    max_tokens: 2000,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setFormData({
        parameter_type_id: config.parameter_type?.id || null,
        enabled: config.enabled || false,
        default_provider: config.default_provider_id || 0,
        prompt_template: config.prompt_template || '',
        model_name: config.model_name || 'gpt-4',
        temperature: config.temperature || 0.7,
        max_tokens: config.max_tokens || 2000,
      });
    } else {
      setFormData({
        parameter_type_id: null,
        enabled: false,
        default_provider: 0,
        prompt_template: `You are an intelligence analysis assistant. Analyze the following OSINT search results and provide a comprehensive summary.

Search Details:
- Search Value: {search_value}
- Parameter Type: {parameter_type}
- Total APIs Called: {total_apis_called}

Search Results Data:
{search_data}

Please provide:
1. A concise executive summary
2. Key findings from each API source
3. Any patterns or correlations detected
4. Recommendations for further investigation

Format your response in clear, structured sections.`,
        model_name: 'gpt-4',
        temperature: 0.7,
        max_tokens: 2000,
      });
    }
  }, [config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!formData.default_provider) {
        throw new Error('Default Provider is required');
      }

      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save AI Summary Config');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {config ? 'Edit AI Summary Config' : 'Create AI Summary Config'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              label="Parameter Type"
              helpText="Leave blank for global default configuration"
            >
              <Select
                value={formData.parameter_type_id || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                  setFormData({ ...formData, parameter_type_id: e.target.value ? parseInt(e.target.value) : null })
                }
              >
                <option value="">Global (Default for all)</option>
                {parameterTypes.map((pt: any) => (
                  <option key={pt.id} value={pt.id}>{pt.display_name}</option>
                ))}
              </Select>
            </FormField>

            <FormField
              label="Default Provider"
              required
            >
              <Select
                value={formData.default_provider || 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                  setFormData({ ...formData, default_provider: parseInt(e.target.value) })
                }
              >
                <option value="0">Select Provider</option>
                {providers.filter(p => p.status === 'active').map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name} ({provider.providerType})
                  </option>
                ))}
              </Select>
            </FormField>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled || false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="h-4 w-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Enabled
              </label>
            </div>

            <FormField
              label="Prompt Template"
              helpText="Use placeholders: {search_value}, {parameter_type}, {total_apis_called}, {search_data}"
            >
              <Textarea
                value={formData.prompt_template || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                  setFormData({ ...formData, prompt_template: e.target.value })
                }
                rows={10}
                placeholder="Enter prompt template..."
              />
            </FormField>

            <FormField
              label="Model Name"
              required
            >
              <Input
                value={formData.model_name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  setFormData({ ...formData, model_name: e.target.value })
                }
                placeholder="e.g., gpt-4, claude-3-opus, gemini-pro"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Temperature"
              >
                <Input
                  type="number"
                  value={formData.temperature || 0.7}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData({ ...formData, temperature: parseFloat(e.target.value) || 0.7 })
                  }
                  min="0"
                  max="2"
                  step="0.1"
                />
              </FormField>

              <FormField
                label="Max Tokens"
              >
                <Input
                  type="number"
                  value={formData.max_tokens || 2000}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 2000 })
                  }
                  min="100"
                  step="100"
                />
              </FormField>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : config ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const AdminAISummaryConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<AISummaryConfig[]>([]);
  const [currentDefault, setCurrentDefault] = useState<AISummaryConfig | null>(null);
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [parameterTypes, setParameterTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<AISummaryConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [configsData, defaultData, providersData, paramTypes] = await Promise.all([
        aiSummaryConfigService.listConfigs(),
        aiSummaryConfigService.getCurrentDefault(),
        aiProvidersService.listProviders(),
        apiService.getParameterTypes(),
      ]);
      
      setConfigs(configsData);
      if (defaultData.exists && defaultData.config) {
        setCurrentDefault(defaultData.config);
      } else {
        setCurrentDefault(null);
      }
      setProviders(providersData);
      setParameterTypes(paramTypes);
    } catch (error: any) {
      showToast('Failed to load AI Summary Configs', 'error');
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: AISummaryConfig) => {
    setEditingConfig(config);
    setIsModalOpen(true);
  };

  const handleSave = async (data: Partial<AISummaryConfigFormData>) => {
    try {
      if (editingConfig) {
        await aiSummaryConfigService.updateConfig(editingConfig.id, data);
        showToast('AI Summary Config updated successfully', 'success');
      } else {
        await aiSummaryConfigService.createConfig(data);
        showToast('AI Summary Config created successfully', 'success');
      }
      await loadData();
      setIsModalOpen(false);
      setEditingConfig(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save AI Summary Config';
      throw new Error(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this AI Summary Config? This action cannot be undone.')) {
      return;
    }

    try {
      await aiSummaryConfigService.deleteConfig(id);
      showToast('AI Summary Config deleted successfully', 'success');
      await loadData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to delete AI Summary Config', 'error');
    }
  };

  const handleSetAsDefault = async (config: AISummaryConfig) => {
    try {
      await aiSummaryConfigService.setAsDefault(config.id);
      showToast('Default provider updated successfully', 'success');
      await loadData();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to set as default', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <PageHeader
        title="AI Summary Configuration"
        subtitle="Manage AI summary generation settings and default provider"
        actionButtonText="Add Configuration"
        onActionButtonClick={handleCreate}
        actionButtonIcon={
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        }
      >
        <MasterDataImportExport resource="ai_summary_configs" onComplete={loadData} />
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Current Default Provider */}
        {currentDefault && (
          <Card className="mb-6 bg-indigo-50/80 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-200">Current Default Provider</h3>
                  <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">
                    <strong>{currentDefault.default_provider_name}</strong> ({currentDefault.model_name})
                  </p>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                    This provider is used for all searches unless a parameter-specific config exists
                  </p>
                </div>
                <Badge className="bg-indigo-600 text-white">
                  Active
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* Configurations List */}
        <Card>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No AI Summary Configurations found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  configs.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {config.is_global ? (
                            <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-800">Global Default</Badge>
                          ) : (
                            config.parameter_type?.display_name || 'N/A'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {config.default_provider_name || 'Not Set'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{config.model_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {config.enabled ? (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800">Enabled</Badge>
                        ) : (
                          <Badge className="bg-red-100 dark:bg-red-900/30 text-red-800">Disabled</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {!config.is_global && (
                            <button
                              onClick={() => handleSetAsDefault(config)}
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900"
                            >
                              Set as Default
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(config)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(config.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <EditModal
        config={editingConfig}
        onSave={handleSave}
        onClose={() => {
          setIsModalOpen(false);
          setEditingConfig(null);
        }}
        isOpen={isModalOpen}
        providers={providers}
        parameterTypes={parameterTypes}
      />
    </div>
  );
};

